import {
    bexHttpClient
} from '../../clients';
import {
    CreateOrganizationRequest,
    CheckOrganizationNameResponse,
    CreateOrganizationResponse,
    GetOrganizationsByUserIdResponse,
    GetSubOrganizationsResponse,
    CheckOrganizationClusterResponse,
    DeleteOrganizationClusterResponse,
    DeleteOrganizationResponse
} from '../bexService.types';

/**
 * Service class for performing organization operations.
 */
class OrganizationService {

    /**
     * Check organization name exists or not.
     * @param {string} organizationName - The name of the organization to check.
     * @returns {Promise<CheckOrganizationNameResponse>} The response from the server returns success if organization name does not exist.
     */
    async checkOrganizationName(organizationName: string): Promise<CheckOrganizationNameResponse> {
        const response = await bexHttpClient.get(`/db/organizations/check-organization-name/${organizationName}`);
        return response;
    }

    /**
     * Create new organization.
     * @param {CreateOrganizationRequest} data - The organization data to create.
     * @returns {Promise<CreateOrganizationResponse>} The response from the server returns new organization object.
     */
    async createOrganization(data: CreateOrganizationRequest): Promise<CreateOrganizationResponse> {
        const response = await bexHttpClient.post('/db/organizations/insert_organization', data);
        return response;
    }

    /**
     * Fetches organization by id.
     * @param {string} userId - The user ID to get organizations for.
     * @returns {Promise<GetOrganizationsByUserIdResponse>} The response from the server returns organization object.
     */
    async getOrganizations(userId: string): Promise<GetOrganizationsByUserIdResponse> {
        const response = await bexHttpClient.get(`/db/organizations/get_organizations_by_user_id/${userId}`);
        return response;
    }

    /**
     * Fetches sub-organization by organizationId.
     * @param {string} organizationId - The organization ID to get sub-organizations for.
     * @returns {Promise<GetSubOrganizationsResponse>} The response from the server returns list of sub-organizations.
     */
    async getSubOrganizations(organizationId: string): Promise<GetSubOrganizationsResponse> {
        const response = await bexHttpClient.get(`/db/sub-organizations/get-suborganizations/${organizationId}`);
        return response;
    }

    /**
     * Update sub-organization data.
     * @param {string} organizationId - The organization ID.
     * @param {string} subOrganizationId - The sub-organization ID.
     * @param {any} data - The data to update the sub-organization with.
     * @returns {Promise<any>} The response from the server returns updated sub-organizations object.
     * Note: Using 'any' because the sub-organization update data structure is not defined in the types file
     */
    async updateSubOrganizations(organizationId: string, subOrganizationId: string, data: any): Promise<any> {
        const response = await bexHttpClient.put(`/db/sub-organizations/update-sub-organization?organizationId=${organizationId}&subOrganizationId=${subOrganizationId}`, data);
        return response;
    }

    /**
     * Get organization users.
     * @param {string} organizationId - The organization ID to get users for.
     * @returns {Promise<any>} The response from the server returns list of organization users.
     * Note: Using 'any' because the organization users response structure is not defined in the types file
     */
    async getOrganizationUsers(organizationId: string): Promise<any> {
        const response = await bexHttpClient.get(`/db/users/getUsers/${organizationId}`);
        return response;
    }

    async getUsersByIds(organizationId: string, userIds: string[]): Promise<any> {
        const response = await bexHttpClient.post('/db/users/getUsersById', {
            organizationId,
            userIds,
        });
        return response;
    }

    /**
     * Check organization cluster status.
     * @param {string} organizationId - The organization ID to check cluster status for.
     * @returns {Promise<CheckOrganizationClusterResponse>} The response from the server returns organization object status.
     */
    async checkOrganizationCluster(organizationId: string): Promise<CheckOrganizationClusterResponse> {
        const response = await bexHttpClient.get(`/db/organizations/check-organization-cluster/${organizationId}`);
        return response;
    }

    /**
     * delete organization cluster.
     * @param {string} organizationId - The organization ID to delete cluster for.
     * @returns {Promise<DeleteOrganizationClusterResponse>} The response from the server returns delete cluster response.
     */
    async deleteOrganizationCluster(organizationId: string): Promise<DeleteOrganizationClusterResponse> {
        const response = await bexHttpClient.delete(`/db/organizations/delete-organization-cluster/${organizationId}`);
        return response;
    }

    /**
     * delete organization.
     * @param {string} organizationId - The organization ID to delete.
     * @returns {Promise<DeleteOrganizationResponse>} The response from the server returns delete organization response.
     */
    async deleteOrganization(organizationId: string): Promise<DeleteOrganizationResponse> {
        const response = await bexHttpClient.delete(`/db/organizations/delete-organization/${organizationId}`);
        return response;
    }

}

const organizationService = new OrganizationService();
export { organizationService };